/**
 * The single owner of "where does this formation member actually go".
 *
 * There is exactly one function here that maps a stage-local
 * `ExpandedTransform` onto the world, and both consumers use it: the runtime,
 * to release each attack, and the evaluator, to build the footprint it
 * prices. That is the point of the module. Placement implemented twice is the
 * failure this feature is most exposed to — a fan that prices as though it
 * spreads while resolving as though it does not, or the reverse — and a
 * passing hit-count test would not catch it. One owner makes the two
 * unable to disagree.
 *
 * ## Frame
 *
 * A transform is expressed in a stage-local frame:
 *
 * ```text
 *   +y  = forward, along the cast's aim
 *   +x  = right of forward
 *   directionRad = rotation off forward, in the same sense as world angles
 *                  (counter-clockwise positive), so it turns AWAY from +x
 * ```
 *
 * The last line is easy to get backwards: `+x` is the right-hand side, and
 * `directionRad` adds to the cast's world angle, so a positive value turns
 * to the left. Tests assert the resulting geometry rather than this
 * sentence.
 *
 * So a 3-member 60° `FAN` carries `directionRad` of −30°, 0°, +30°, and a
 * `PARALLEL` formation offsets members along `x` without turning them.
 */
import type { Vec2 } from './state.js';
import { formationIsWorldFixed, type ExpandedTransform } from '../skill-engine/composite-types.js';

/** The cast's own pose at the moment of release. */
export interface CastPose {
  /** Where the cast releases from — the caster's position at the release tick. */
  readonly origin: Vec2;
  /** The cast's aim, as a rotation in world space. */
  readonly aimRad: number;
}

/** One attack unit's resolved pose in world space. */
export interface AttackPose {
  readonly origin: Vec2;
  readonly directionRad: number;
  /** Unit vector form of `directionRad`, for callers that want a direction. */
  readonly direction: Vec2;
}

/**
 * The cast pose under which world coordinates coincide with stage-local
 * ones, for consumers that only need relative geometry — the evaluator
 * prices a uniform enemy field, which is invariant under rigid motion, so it
 * works in this frame rather than inventing a caster.
 *
 * `aimRad` is a quarter turn, not zero, and that is deliberate: the local
 * frame calls **+y** forward, so the identity is the pose whose forward
 * points along world +y. At `aimRad = 0` the cast aims along world +x and
 * local `(x, y)` would land at world `(y, −x)` — a correct rigid motion, but
 * a confusing thing to call "identity". Pinning it here means the evaluator
 * and the runtime can share one function with no coordinate reinterpretation
 * at the boundary.
 */
export const IDENTITY_CAST_POSE: CastPose = Object.freeze({
  origin: Object.freeze({ x: 0, y: 0 }) as Vec2,
  aimRad: Math.PI / 2,
});

/**
 * Place one formation member in the world.
 *
 * Rigid motion only: the cast's aim rotates the member's facing and its
 * lateral/forward offsets, and the cast's origin translates it. Nothing here
 * scales, mirrors or clamps, so a formation's authored shape survives
 * placement exactly — a 60° spread is 60° wherever the caster is standing
 * and whichever way they face.
 */
export function resolveAttackPose(cast: CastPose, transform: ExpandedTransform): AttackPose {
  const cos = Math.cos(cast.aimRad);
  const sin = Math.sin(cast.aimRad);

  // Forward is the aim; right is forward turned a quarter-turn toward +x.
  const forward = { x: cos, y: sin };
  const right = { x: sin, y: -cos };

  const origin: Vec2 = {
    x: cast.origin.x + right.x * transform.x + forward.x * transform.y,
    y: cast.origin.y + right.y * transform.x + forward.y * transform.y,
  };

  const directionRad = cast.aimRad + transform.directionRad;
  return {
    origin,
    directionRad,
    direction: { x: Math.cos(directionRad), y: Math.sin(directionRad) },
  };
}

/**
 * The pose one stage's members are placed from. A scattered formation is a
 * fixed set of points round the caster, so it is laid down in the same world
 * orientation (`+x`) every cast, whichever way the caster aims or moves;
 * every other formation turns with the cast's aim.
 */
export function stageCastPose(cast: CastPose, formationKind: string): CastPose {
  return formationIsWorldFixed(formationKind) ? { origin: cast.origin, aimRad: 0 } : cast;
}

/**
 * True when a transform asks for no placement at all.
 *
 * A migrated single-attack skill must behave byte-identically to how it
 * behaved before placement existed, so the executor detects this case and
 * leaves the cast's own targeting completely untouched rather than
 * round-tripping it through a rotation that would introduce float drift.
 */
export function isIdentityTransform(transform: ExpandedTransform): boolean {
  return transform.x === 0 && transform.y === 0 && transform.directionRad === 0;
}

/**
 * Formations whose world placement P6a has actually verified.
 *
 * `SINGLE`, `FAN`, `PARALLEL` and `RADIAL`. All four are pure rigid motions
 * of the cast pose: `FAN` and `RADIAL` turn a member without moving it,
 * `PARALLEL` slides it sideways without turning it, and `SINGLE` does
 * neither.
 *
 * `RING` joins them: its members stand on the radius and face outward,
 * ratified 2026-09-19, so position and facing now come from one idea
 * instead of disagreeing.
 *
 * `SCATTER` is still withheld — it belongs with the meteor work, whose
 * anchoring is its own question. Opening it by omission is what this list
 * prevents.
 */
export const WORLD_PLACED_FORMATION_KINDS = Object.freeze(['SINGLE', 'FAN', 'PARALLEL', 'RADIAL', 'RING'] as const);

export function formationIsWorldPlaced(kind: string): boolean {
  return (WORLD_PLACED_FORMATION_KINDS as readonly string[]).includes(kind);
}

/**
 * How a delivery is told where to go.
 *
 * - `AIM` — the heading is the governing input. A projectile flies along it
 *   and a cone opens about it, so rotating a member rotates exactly what
 *   was asked. Placed by handing the runtime a *direction*.
 * - `POINT` — the delivery is anchored at a ground position. A `ground_aoe`
 *   is this: handing it a direction makes the runtime project the circle
 *   forward by its own radius, relocating it by `radiusM` — a translation
 *   the authored transform never asked for. Handing it the resolved
 *   *point* puts the impact exactly where the formation placed it, which
 *   is what a meteor needs.
 *
 * Both modes read the same `resolveAttackPose` output; they differ only in
 * which half of it the runtime is told. A delivery in neither mode has no
 * defined placement and is refused rather than guessed at.
 */
export type DeliveryPlacementMode = 'AIM' | 'POINT';

const DELIVERY_PLACEMENT_MODES: Readonly<Record<string, DeliveryPlacementMode>> = Object.freeze({
  projectile: 'AIM',
  cone: 'AIM',
  ground_aoe: 'POINT',
});

export function deliveryPlacementMode(type: string): DeliveryPlacementMode | undefined {
  return DELIVERY_PLACEMENT_MODES[type];
}

export function deliveryIsWorldPlaced(type: string): boolean {
  return deliveryPlacementMode(type) !== undefined;
}

export const WORLD_PLACED_DELIVERY_TYPES = Object.freeze(Object.keys(DELIVERY_PLACEMENT_MODES));
